import { OnboardingUserValues } from './values';
import { ValidatedSection } from './validation';

export type SectionSummaryItem = {
    title: string;
    lite?: boolean;
    value?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
};

export type Summary = {
    [key: string]: {
        title: string;
        validation?: ValidatedSection;
        lite?: boolean;
        items: {
            [key in keyof Partial<OnboardingUserValues>]: SectionSummaryItem;
        };
    };
};

const getSummary = (): Summary => ({
    personalDetails: {
        title: 'Personal Details',
        lite: true,
        items: {
            name: {
                title: 'Full Name',
                lite: true,
            },
            email: {
                title: 'Email Address',
                lite: true,
            },
            phoneNumber: {
                title: 'Mobile Phone Number',
                lite: true,
            },
            dateOfBirth: {
                title: 'Date of Birth',
                lite: true,
            },
            niNumber: {
                title: 'National Insurance Number',
            },
            addressLine1: {
                title: 'Address Line 1',
            },
            addressLine2: {
                title: 'Address Line 2',
            },
            town: {
                title: 'Town',
            },
            county: {
                title: 'County',
            },
            postCode: {
                title: 'Post Code',
            },
            emergencyContact: {
                title: 'Emergency Contact',
            },
            deviceType: {
                title: 'Device which will have the Doc Abode app installed?',
            },
        },
    },
    financialDetails: {
        title: 'Financial Details',
        items: {
            nameOnAccount: {
                title: 'Name on Account',
            },
            accountNumber: {
                title: 'Account Number',
            },
            sortCode: {
                title: 'Sort Code',
            },
            tradingStatus: {
                title: 'How are you engaging with Doc Abode or the healthcare provider?',
            },
            inNHSPension: {
                title: 'Are you currently a member of the NHS pension scheme?',
            },
            pensionSDNumber: {
                title: 'Pension SD number',
            },
            pensionTierDeduction: {
                title: 'Pension scheme tier % deduction',
            },
            companyName: {
                title: 'Company Name',
            },
            companyRegistrationNumber: {
                title: 'Company Registration Number',
            },
            vatRegistered: {
                title: 'Are you VAT registered?',
            },
            vatRegistrationNumber: {
                title: 'VAT Registration Number',
            },
        },
    },
    equalityAndDiversity: {
        title: 'Equality and Diversity',
        items: {
            equalityGender: {
                title: 'Gender',
            },
            equalityGenderOther: {
                title: 'You can specify your gender here',
            },
            equalityMaritalStatus: {
                title: 'Marital Status',
            },
            equalityEthnicOrigin: {
                title: 'Ethnic Origin',
            },
            equalityCountryOfBirth: {
                title: 'Country of Birth',
            },
            equalityNationality: {
                title: 'Nationality',
            },
            equalityDisability: {
                title: 'Disability',
            },
            equalityReligiousBeliefs: {
                title: 'Religious Beliefs',
            },
            equalitySexualOrientation: {
                title: 'Sexual Orientation',
            },
        },
    },
    preferences: {
        title: 'Doc Abode Preferences',
        lite: true,
        items: {
            specialities: {
                title: 'Specialities',
                lite: true,
            },
            specialitiesOther: {
                title: 'Specialities (other)',
                lite: true,
            },
            languages: {
                title: 'Languages Spoken',
                lite: true,
            },
            languagesOther: {
                title: 'Languages Spoken (other)',
                lite: true,
            },
        },
    },
    professionalDetails: {
        title: 'Professional Details',
        lite: true,
        items: {
            professionalBodyRegistrationRequired: {
                title: 'Does your role require you to be registered with a professional body?',
            },
            professionalBodyType: {
                title: 'Professional Body',
            },
            professionalBodyId: {
                title: 'Professional Body ID',
            },
            primaryGPFunction: {
                title: 'Primary GP Function',
            },
            gmcRegistrationDate: {
                title: 'GMC Registration Date',
            },
            confirmComplaints: {
                title:
                    'I am not aware of any complaints against me that have been referred to independent review by NHSE or the GMC',
            },
            confirmRestrictions: {
                title: 'I have no restrictions on practice or changes in GMC registration status',
            },
            confirmOnPerformersList: {
                title: 'I am currently included on the NHS England Performers List',
            },
            confirmNotPerformersListSuspended: {
                title: 'I have not been suspended from the NHS England Performers List',
            },
            confirmObligations: {
                title:
                    'I accept the professional obligations placed on me in Good Medical Practice paragraphs 28-30 (GMC 2013) about my personal health',
            },
            confirmAcceptTerms: {
                title: 'I accept the Doc Abode Terms and Conditions',
            },
            practiceName: {
                title: 'Practice Name',
            },
            practiceODSCode: {
                title: 'Practice ODS Code',
            },
            nhsSmartcardNo: {
                title: 'NHS Smartcard Number',
            },
            nhsEmail: {
                title: 'NHS Email Address',
            },
            medicalIndemnityOrganisation: {
                title: 'Please state your Medical Indemnity Organisation',
                lite: true,
            },
        },
    },
    transport: {
        title: 'Transport',
        items: {
            hasCurrentDrivingLicence: {
                title: 'Do you possess a current driving licence?',
                lite: true,
            },
            drivingLicenceNumber: {
                title: 'Driving licence number',
                lite: true,
            },
            hasCarBusinessInsurance: {
                title: 'Do you hold valid business insurance for your car?',
                lite: true,
            },
            hasConviction: {
                title:
                    'Have you ever been convicted of an offence (including motoring but not parking offences) or are any charges or summons at present outstanding against you?',
                lite: true,
            },
            convictionDetails: {
                title: 'Please give details of the conviction',
                lite: true,
            },
        },
    },
    firstReference: {
        title: 'First Reference',
        items: {
            reference1FullName: {
                title: 'Full name',
            },
            reference1Address1: {
                title: 'Address line 1',
            },
            reference1Address2: {
                title: 'Address line 2',
            },
            reference1Town: {
                title: 'Town',
            },
            reference1County: {
                title: 'County',
            },
            reference1PostCode: {
                title: 'Post code',
            },
            reference1Telephone: {
                title: 'Telephone number',
            },
            reference1Email: {
                title: 'Email address',
            },
            reference1EmailConfirm: {
                title: 'Confirm email address',
            },
        },
    },
    secondReference: {
        title: 'Second Reference',
        items: {
            reference2FullName: {
                title: 'Full name',
            },
            reference2Address1: {
                title: 'Address line 1',
            },
            reference2Address2: {
                title: 'Address line 2',
            },
            reference2Town: {
                title: 'Town',
            },
            reference2County: {
                title: 'County',
            },
            reference2PostCode: {
                title: 'Post code',
            },
            reference2Telephone: {
                title: 'Telephone number',
            },
            reference2Email: {
                title: 'Email address',
            },
            reference2EmailConfirm: {
                title: 'Confirm email address',
            },
        },
    },
    supportingDocumentation: {
        title: 'Supporting Documentation',
        items: {
            vaccinations: {
                title: 'Please select which routine vaccinations you have received',
            },
            covidStatus: {
                title: 'Latest COVID-19 status',
            },
        },
    },
    trainingAndDevelopment: {
        title: 'Training and Development',
        items: {
            training: {
                title: 'Training Received',
            },
            elfhRegistered: {
                title: 'Are you registered on www.e-lfh.co.uk?',
            },
            systemProficiency: {
                title: 'Please confirm which systems you are proficient with',
            },
            systemProficiencyOther: {
                title: 'Please confirm which systems you are proficient with (other)',
            },
        },
    },
    declaration: {
        title: 'Declaration',
        lite: true,
        items: {
            confirmCorrect: {
                title:
                    'I confirm that the information I have provided is correct and understand that misleading statements may be sufficient grounds for cancelling any agreements made',
                lite: true,
            },
            confirmUnansweredFollowup: {
                title:
                    'I understand that questions left unanswered or partially answered may be discussed following this application',
                lite: true,
            },
            receiveMarketing: {
                title:
                    'I am happy to receive marketing emails from Doc Abode in line with the Privacy Policy.',
                lite: true,
            },
            confirmBasicAdvancedLifeSupport: {
                title:
                    'I hereby confirm that I am currently up to date in regard to mandatory training in the following areas: Basic/Advanced Life Support',
            },
            confirmSafeguardingAdults: {
                title:
                    'I hereby confirm that I am currently up to date in regard to mandatory training in the following areas: Safeguarding Adults',
            },
            confirmSafeguardingChildren: {
                title:
                    'I hereby confirm that I am currently up to date in regard to mandatory training in the following areas: Safeguarding Children',
            },
        },
    },
});

export default getSummary;
